import { useCallback, useEffect, useState } from 'react';

import type { MapLocalStorageKey } from '../components/Maps/constants/constants';
import { reportMykissClientError } from '@lib/errors/client';
import { useClientHasRendered } from '@domain/session/useClientHasRendered';

export type LocalStorageKey = MapLocalStorageKey;

const getItemFromLocalStorage = <T>(
  key: LocalStorageKey,
  defaultValue: T,
  options: {
    // use session storage instead of local storage
    useSessionStorage: boolean;
  },
): T => {
  try {
    const storage = options.useSessionStorage ? sessionStorage : localStorage;
    const item = storage.getItem(key);

    if (item === null) {
      return defaultValue;
    }
    return JSON.parse(item);
  } catch (error) {
    reportMykissClientError(error, 'getItemFromLocalStorage', 'warning');
    return defaultValue;
  }
};

// eslint-disable-next-line @typescript-eslint/no-unnecessary-type-parameters
const saveItemToLocalStorage = <T>(
  key: LocalStorageKey,
  value: T,
  options: { useSessionStorage: boolean },
): void => {
  try {
    const storage = options.useSessionStorage ? sessionStorage : localStorage;
    storage.setItem(key, JSON.stringify(value));
  } catch (error) {
    reportMykissClientError(error, 'saveItemToLocalStorage', 'info');
  }
};

export const useLocalStorage = <T>(
  key: LocalStorageKey,
  defaultValue: T,
  options?: { useSessionStorage?: boolean },
): { state: [T, (newValue: T) => void]; isLoaded: boolean } => {
  const [value, setValue] = useState<T>(defaultValue);
  // needed if we need to do some data manipulation immediately after load
  const [isLoaded, setIsLoaded] = useState(false);
  const clientHasRendered = useClientHasRendered();
  const useSessionStorage = options?.useSessionStorage ?? false;

  const setValueAndSaveItToLocalStorage = useCallback(
    (newValue: T): void => {
      saveItemToLocalStorage(key, newValue, { useSessionStorage });
      setValue(getItemFromLocalStorage(key, defaultValue, { useSessionStorage }));
    },
    [defaultValue, key, useSessionStorage],
  );

  useEffect(() => {
    if (clientHasRendered && !isLoaded) {
      setValueAndSaveItToLocalStorage(
        getItemFromLocalStorage(key, defaultValue, { useSessionStorage }),
      );
      setIsLoaded(true);
    }
    // we only want to trigger this once (after the client has rendered for the first time)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientHasRendered]);

  return { state: [value, setValueAndSaveItToLocalStorage], isLoaded };
};
